import React, { useState } from "react";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";

import { Button, CreateTableNew as CreateTable } from "../../../common/APICommon";
import { ConfirmationModal } from "../../../common/Modal";
import { AddEditCrewDefinitionModal } from "../shared/AddEditCrewDefinitionModal";

/**
 * Tab 4 — Crew Definition
 *
 * REFACTORED: Simplified handleAdd and handleUpdate
 * - No longer needs to look up codes/descriptions from options
 * - Pickers already provide all necessary data
 */
export const CrewDefinitionTab = ({ form, set, options, isViewOnly, errors = {} }) => { // MODIFIED: added errors prop for consistency
	const rows = form.crewDefinitions || [];
	const editable = !isViewOnly;

	const [currentPage, setCurrentPage] = useState(1);

	// ── Modal state ───────────────────────────────────────────────────────────
	const [showModal, setShowModal] = useState(false);
	const [modalMode, setModalMode] = useState("add");
	const [selectedRow, setSelectedRow] = useState(null);

	// ── Delete confirmation ───────────────────────────────────────────────────
	const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
	const [deleteIndex, setDeleteIndex] = useState(null);

	const updateRows = (updated) => set("crewDefinitions", updated);

	// ── Open Add ──────────────────────────────────────────────────────────────
	const openAdd = () => {
		setModalMode("add");
		setSelectedRow(null);
		setShowModal(true);
	};

	// ── Open Edit ─────────────────────────────────────────────────────────────
	const openEdit = (row) => {
		setModalMode("edit");
		setSelectedRow(row);
		setShowModal(true);
	};

	// ── Handlers passed to modal ──────────────────────────────────────────────
	// REFACTORED: No longer needs options lookup — pickers provide all data
	const handleAdd = (rowForm) => {
		updateRows([
			...rows,
			{
				id: null,
				departmentId: rowForm.departmentId,
				departmentCode: rowForm.departmentCode, // Already provided by picker
				departmentDescription: rowForm.departmentDescription, // Already provided by picker
				positionId: rowForm.positionId,
				positionCode: rowForm.positionCode, // Already provided by picker
				positionDescription: rowForm.positionDescription, // Already provided by picker
				allowedCrews: rowForm.allowedCrews,
			},
		]);
	};

	// REFACTORED: No longer needs options lookup — pickers provide all data
	const handleUpdate = (rowForm, index) => {
		updateRows(
			rows.map((r, i) =>
				i === index
					? {
						...r,
						departmentId: rowForm.departmentId,
						departmentCode: rowForm.departmentCode, // Already provided by picker
						departmentDescription: rowForm.departmentDescription, // Already provided by picker
						positionId: rowForm.positionId,
						positionCode: rowForm.positionCode, // Already provided by picker
						positionDescription: rowForm.positionDescription, // Already provided by picker
						allowedCrews: rowForm.allowedCrews,
					}
					: r
			)
		);
	};

	// ── Delete ────────────────────────────────────────────────────────────────
	const confirmDelete = (row) => {
		setDeleteIndex(row._index);
		setShowDeleteConfirm(true);
	};

	const handleDelete = () => {
		updateRows(rows.filter((_, i) => i !== deleteIndex));
		setShowDeleteConfirm(false);
		setDeleteIndex(null);
	};

	// ── Table config ──────────────────────────────────────────────────────────
	const headers = [
		"Dept. Code",
		"Department",
		"Position Code",
		"Position",
		"Allowed Crews",
		...(editable ? ["Actions"] : []),
	];
	const accessors = [
		"departmentCode",
		"departmentDescription",
		"positionCode",
		"positionDescription",
		"allowedCrews",
		...(editable ? ["actions"] : []),
	];

	const tableData = rows.map((row, index) => ({
		...row,
		_index: index,
	}));

	const actions = editable
		? [
			{
				name: "Edit",
				icon: <EditIcon fontSize="small" />,
				className: "bg-green-500 hover:bg-green-600 flex items-center",
				function: (row) => openEdit(row),
				visible: true,
			},
			{
				name: "Delete",
				icon: <DeleteIcon fontSize="small" />,
				className: "bg-red-500 hover:bg-red-600 flex items-center",
				function: (row) => confirmDelete(row),
				visible: true,
			},
		]
		: [];

	return (
		<div className="space-y-4 pt-2">
			<div className="bg-gray-50 p-6 rounded-xl border border-gray-200 text-gray-500">
				<div className="flex items-center justify-between mb-4">
					<span className="text-xs font-bold uppercase tracking-widest text-gray-400">
						Crew Definition
					</span>
					{editable && (
						<Button
							icon={<AddIcon />}
							name="Add Row"
							className="bg-customGreen hover:bg-customGreenHover border-none cursor-pointer"
							onClick={openAdd}
						/>
					)}
				</div>

				<CreateTable
					tableName="CrewDefinitionTable"
					headers={headers}
					accessors={accessors}
					data={tableData}
					actions={actions}
					currentPage={currentPage}
					setCurrentPage={setCurrentPage}
					itemsPerPage={10}
					totalItems={tableData.length}
				/>
			</div>

			<AddEditCrewDefinitionModal
				isOpen={showModal}
				onClose={() => setShowModal(false)}
				mode={modalMode}
				selectedData={selectedRow}
				onAdd={handleAdd}
				onUpdate={handleUpdate}
				options={options} // KEPT for backward compatibility (not used by pickers)
			/>

			<ConfirmationModal
				visible={showDeleteConfirm}
				onClose={() => setShowDeleteConfirm(false)}
				onConfirm={handleDelete}
				title="Delete Row"
				message="Are you sure you want to delete this crew definition?"
			/>
		</div>
	);
};